/* TokenManager (functional): reusable, framework-agnostic auth/refresh coordinator 
 * Implementation: Steps 4, 5, 8, 10 - Token refresh coordination
 */
import debug from 'debug';
import { CircuitBreaker } from './circuitBreaker';
import { hasUsableRefreshToken } from './refreshTokenUtils';
import {
  EXTERNAL_REFRESH_TIMEOUT_MS,
  CIRCUIT_BREAKER_TIMEOUT_MS,
  CIRCUIT_BREAKER_THRESHOLD,
  TOKEN_SKEW_MS,
} from '../config/auth.config';

// Create debug namespaces for different aspects of token management
const debugToken = debug('auth:token');
const debugRefresh = debug('auth:refresh');
const debugStep4 = debug('impl:step4:external-timeout');
const debugStep5 = debug('impl:step5:single-flight');
const debugStep8 = debug('impl:step8:offline');
const debugStep10 = debug('impl:step10:circuit');
const debugCrossTabs = debug('auth:cross-tabs');
const debugError = debug('auth:error');
const debugOffline = debug('auth:offline');

export function createTokenManager(options) {
  const EXTERNAL_REFRESH_TIMEOUT = EXTERNAL_REFRESH_TIMEOUT_MS; // prevent hanging if leader crashes
  
  debugToken('creating token manager', { 
    channelName: options.channelName,
    skewMs: options.skewMs 
  });

  // ✅ STEP 10: Circuit breaker to prevent retry storms
  // Configuration: Can be overridden via options or environment variables
  const circuitBreakerThreshold = options.circuitBreakerThreshold || CIRCUIT_BREAKER_THRESHOLD;
  const circuitBreakerTimeout = options.circuitBreakerTimeout || CIRCUIT_BREAKER_TIMEOUT_MS;

  debugStep10('step 10: initializing circuit breaker', {
    threshold: circuitBreakerThreshold,
    timeout: circuitBreakerTimeout
  });
  const circuitBreaker = new CircuitBreaker({
    threshold: parseInt(circuitBreakerThreshold, 10),
    timeout: parseInt(circuitBreakerTimeout, 10),
    name: 'TokenRefresh',
  });

  const state = {
    refreshFn: options.refreshFn, // () => Promise<tokenInfo>
    getAuthData: options.getAuthData, // () => { token, refresh, exp, refresh_expires_in? }
    setAuthData: options.setAuthData, // (tokenInfo) => void
    clearAuthData: options.clearAuthData, // () => void
  skewMs: options.skewMs || TOKEN_SKEW_MS,
    onLogout: options.onLogout || (() => {}),
    onRefreshed: options.onRefreshed || (() => {}),
    channelName: options.channelName || 'auth_tokens',

    // Cross-tab state
    externalRefreshInFlight: null,
    _externalResolve: null,
    _externalReject: null,
    _externalRefreshTimeoutId: null, // Track timeout for cleanup
    _post: null,
    _closeChannel: null,
    // In-tab single-flight promise for refresh coalescing
    _inFlightRefresh: null,

    // Offline detection state
    _onlineHandler: null,
    _offlineHandler: null,
  };

  /**
   * Creates a message handler for cross-tab communication.
   * Handles refresh:start, refresh:success, and refresh:fail messages.
   * 
   * @param {Object} state - Token manager state
   * @param {Function} resetExternal - Function to reset external refresh state
   * @param {Function} api - Token manager API
   * @param {CircuitBreaker} circuitBreaker - Circuit breaker instance
   * @param {number} EXTERNAL_REFRESH_TIMEOUT - Timeout for external refresh
   * @returns {Function} Message handler function
   */
  function createMessageHandler(state, resetExternal, api, circuitBreaker, EXTERNAL_REFRESH_TIMEOUT) {
    return (data) => {
      if (!data) return;
      
      debugCrossTabs('Received cross-tab message: %s', data.type);
      
      if (data.type === 'refresh:start') {
        if (!state.externalRefreshInFlight) {
          debugCrossTabs('External refresh started, creating promise');
          state.externalRefreshInFlight = new Promise((res, rej) => {
            // Setup timeout to prevent hanging if leader crashes
            state._externalRefreshTimeoutId = setTimeout(async () => {
              debugStep4('step 4: external refresh timeout expired (%dms) - taking over as leader', EXTERNAL_REFRESH_TIMEOUT);
              debugCrossTabs('External refresh timeout - taking over as leader');
              
              // Clear external state so we can take over
              resetExternal();
              
              // Take over leadership and refresh ourselves
              try {
                // ✅ STEP 10: Wrap timeout takeover with circuit breaker
                debugStep10('step 10: executing timeout takeover through circuit breaker');
                const tokens = await circuitBreaker.execute(async () => {
                  return await state.refreshFn();
                });

                debugStep4('step 4: timeout takeover successful, persisting tokens');
                api.persist(tokens);
                api.broadcastSuccess(tokens);
                res(tokens);
              } catch (error) {
                debugError('Failed to take over after timeout: %O', error);
                api.broadcastFail();
                rej(error);
              }
            }, EXTERNAL_REFRESH_TIMEOUT);
            
            state._externalResolve = (tokens) => {
              // Cancel timeout - leader succeeded
              if (state._externalRefreshTimeoutId) {
                clearTimeout(state._externalRefreshTimeoutId);
                state._externalRefreshTimeoutId = null;
              }

              try {
                api.persist(tokens);
                debugCrossTabs('External refresh succeeded, tokens persisted');
                resetExternal();
                res(tokens);
              } catch (error) {
                // If persist fails (e.g., validation error), treat as refresh failure
                debugError('Failed to persist tokens from external refresh: %O', error);
                resetExternal();
                rej(error);
              }
            };
            state._externalReject = () => {
              // Cancel timeout - leader explicitly failed
              if (state._externalRefreshTimeoutId) {
                clearTimeout(state._externalRefreshTimeoutId);
                state._externalRefreshTimeoutId = null;
              }
              
              debugCrossTabs('External refresh failed');
              resetExternal();
              rej(new Error('refresh failed in leader tab'));
            };
          });
        }
      } else if (data.type === 'refresh:success' && state._externalResolve) {
        debugCrossTabs('External refresh success received');
        state._externalResolve(data.tokens);
      } else if (data.type === 'refresh:fail' && state._externalReject) {
        debugCrossTabs('External refresh failure received');
        state._externalReject();
      }
    };
  }

  /**
   * Attempts to initialize a BroadcastChannel for cross-tab communication.
   * Returns null if BroadcastChannel is not available or initialization fails.
   * 
   * @param {string} channelName - Name of the BroadcastChannel
   * @returns {BroadcastChannel|null} Initialized channel or null
   */
  function initBroadcastChannel(channelName) {
    try {
      const bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(channelName) : null;
      if (bc) {
        debugCrossTabs('BroadcastChannel initialized successfully');
      } else {
        debugCrossTabs('BroadcastChannel not available, using localStorage fallback');
      }
      return bc;
    } catch (error) {
      debugError('Failed to initialize BroadcastChannel, falling back to localStorage: %O', error);
      return null;
    }
  }

  /**
   * Sets up BroadcastChannel with message handling and cleanup.
   * Configures the _post function to send messages via BroadcastChannel.
   * 
   * @param {BroadcastChannel} bc - BroadcastChannel instance
   * @param {Object} state - Token manager state
   * @param {Function} messageHandler - Function to handle incoming messages
   */
  function setupBroadcastChannel(bc, state, messageHandler) {
    // Setup post function for BroadcastChannel
    state._post = (msg) => {
      try { 
        bc.postMessage(msg);
        debugCrossTabs('Broadcast message sent: %s', msg.type);
      } catch (error) {
        debugError('Failed to broadcast message: %O', error);
      }
    };

    // Setup message listener
    bc.addEventListener('message', (e) => {
      try {
        messageHandler(e?.data);
      } catch (error) {
        debugError('Error handling BroadcastChannel message: %O', error);
      }
    });

    // Setup cleanup function
    state._closeChannel = () => { 
      try { 
        bc.close();
        debugCrossTabs('BroadcastChannel closed');
      } catch (error) {
        debugError('Error closing BroadcastChannel: %O', error);
      }
    };
  }

  /**
   * Sets up localStorage fallback for cross-tab communication.
   * Uses storage events to simulate BroadcastChannel behavior.
   * 
   * @param {Object} state - Token manager state
   * @param {Function} messageHandler - Function to handle incoming messages
   */
  function setupStorageFallback(state, messageHandler) {
    const BC_FALLBACK_KEY = '__auth_tokens_bc__';

    // Setup post function for localStorage fallback
    state._post = (msg) => {
      // Performance monitoring for localStorage fallback (synchronous operation)
      // Note: Safari < 15.4 and other legacy browsers without BroadcastChannel use this fallback - may block main thread
      try {
        if (typeof performance !== 'undefined' && performance.mark) {
          performance.mark('auth:storage-fallback-start');
        }

        localStorage.setItem(BC_FALLBACK_KEY, JSON.stringify({ msg, ts: Date.now() }));
        localStorage.removeItem(BC_FALLBACK_KEY);

        if (typeof performance !== 'undefined' && performance.mark && performance.measure) {
          performance.mark('auth:storage-fallback-end');
          performance.measure('auth:storage-fallback',
            'auth:storage-fallback-start',
            'auth:storage-fallback-end');
        }

        debugCrossTabs('Fallback message sent via localStorage: %s (performance measured)', msg.type);
      } catch (error) {
        debugError('Failed to send fallback message via localStorage: %O', error);
      }
    };

    // Setup storage event listener
    const storageHandler = (e) => {
      if (e.key !== BC_FALLBACK_KEY || !e.newValue) return;
      try { 
        const parsed = JSON.parse(e.newValue);
        messageHandler(parsed.msg);
      } catch (error) {
        debugError('Error parsing localStorage fallback message: %O', error);
      }
    };

    window.addEventListener('storage', storageHandler);

    // Setup cleanup function
    state._closeChannel = () => { 
      try { 
        window.removeEventListener('storage', storageHandler);
        debugCrossTabs('Storage event listener removed');
      } catch (error) {
        debugError('Error removing storage event listener: %O', error);
      }
    };
  }

  function resetExternal() {
    debugCrossTabs('Resetting external refresh state');

    // Clear timeout if still pending
    if (state._externalRefreshTimeoutId) {
      clearTimeout(state._externalRefreshTimeoutId);
      state._externalRefreshTimeoutId = null;
    }

    state.externalRefreshInFlight = null;
    state._externalResolve = null;
    state._externalReject = null;
  }

  // Offline event handler - abort in-flight refresh
  function handleOffline() {
    debugOffline('Device went offline');

    // Abort any in-flight refresh
    if (state._inFlightRefresh) {
      debugOffline('Aborting in-flight refresh due to offline');
      api.broadcastFail();
      state._inFlightRefresh = null;
    }
  }

  // Online event handler - auto-refresh if token expired
  async function handleOnline() {
    debugOffline('Device came back online');

    const auth = api.getAuthData();
    if (!auth?.token) {
      debugOffline('No token available, skipping auto-refresh');
      return;
    }

    const now = Date.now();
    const isExpired = auth.exp <= now;
    const canRefresh = api.canRefresh(auth, now);

    if (isExpired && canRefresh) {
      debugOffline('Token expired while offline, auto-refreshing now');
      try {
        await api.refreshSingleFlight();
        debugOffline('Auto-refresh after coming online succeeded');
      } catch (error) {
        debugError('Auto-refresh after coming online failed: %O', error);
      }
    } else if (isExpired && !canRefresh) {
      debugOffline('Both tokens expired while offline, logging out');
      api.logout();
    } else {
      debugOffline('Token still valid, no refresh needed');
    }
  }

  const api = {
    // Exposed, mutable hooks
    onLogout: state.onLogout,
    onRefreshed: state.onRefreshed,
    skewMs: state.skewMs,

    // Auth data access
    getAuthData() {
      try {
        return state.getAuthData?.() || null;
      } catch (error) {
        debugError('Error getting auth data: %O', error);
        return null;
      }
    },

    hasUsableRefreshToken(auth) {
      return hasUsableRefreshToken(auth?.refreshToken);
    },

    canRefresh(auth, now = Date.now()) {
      return api.hasUsableRefreshToken(auth) && auth.refresh > now;
    },

    // Initialize offline detection event listeners
    initialize() {
      debugOffline('Initializing offline detection');

      // Create bound handlers for cleanup
      state._offlineHandler = handleOffline.bind(null);
      state._onlineHandler = handleOnline.bind(null);

      // Register event listeners
      if (typeof window !== 'undefined') {
        window.addEventListener('offline', state._offlineHandler);
        window.addEventListener('online', state._onlineHandler);
        debugOffline('Offline/online event listeners registered');
      }
    },

    // Lifecycle
    dispose() {
      try {
        // Clear any pending external refresh timeout
        if (state._externalRefreshTimeoutId) {
          clearTimeout(state._externalRefreshTimeoutId);
          state._externalRefreshTimeoutId = null;
        }

        // Remove offline/online event listeners
        if (typeof window !== 'undefined') {
          if (state._offlineHandler) {
            window.removeEventListener('offline', state._offlineHandler);
            state._offlineHandler = null;
          }
          if (state._onlineHandler) {
            window.removeEventListener('online', state._onlineHandler);
            state._onlineHandler = null;
          }
          debugOffline('Offline/online event listeners removed');
        }

        state._closeChannel?.();
        debugToken('TokenManager disposed');
      } catch (error) {
        debugError('Error disposing TokenManager: %O', error);
      }
    },

    // Coordination
    broadcastStart() { 
      debugCrossTabs('Broadcasting refresh start');
      state._post?.({ type: 'refresh:start' }); 
    },
    broadcastSuccess(tokens) { 
      debugCrossTabs('Broadcasting refresh success');
      state._post?.({ type: 'refresh:success', tokens }); 
    },
    broadcastFail() { 
      debugCrossTabs('Broadcasting refresh failure');
      state._post?.({ type: 'refresh:fail' }); 
    },

    persist(tokenInfo) {
      // ⚠️ CRITICAL: Always write to storage BEFORE broadcasting to prevent race conditions
      // Other tabs may receive the broadcast and try to read from storage immediately

      // Let validation errors propagate - they indicate malformed tokens from backend
      // that should trigger refresh failure and eventual logout
      state.setAuthData?.(tokenInfo);
      debugToken('Token data persisted successfully');

      // Keep try-catch for user callback - it shouldn't crash the token refresh flow
      try {
        api.onRefreshed?.(tokenInfo);
        debugToken('onRefreshed callback executed');
      } catch (error) {
        debugError('Error in onRefreshed callback: %O', error);
      }
    },

    logout() {
      const stack = new Error().stack;
      const caller = stack?.split('\n')[2]?.trim() || 'unknown';
      const fullStack = stack?.split('\n').slice(1, 6).map(line => line.trim()).join(' > ') || 'unknown';

      debugToken('⚠️ LOGOUT CALLED', {
        caller,
        fullStack,
        timestamp: new Date().toISOString()
      });
      console.warn('[TokenManager] LOGOUT CALLED', {
        caller,
        fullStack,
        timestamp: new Date().toISOString()
      });

      try {
        state.clearAuthData?.();
        debugToken('clearAuthData completed');
      } catch (error) {
        debugError('Error clearing auth data: %O', error);
      } finally {
        try {
          api.onLogout?.();
          debugToken('onLogout callback completed');
        } catch (error) {
          debugError('Error in onLogout callback: %O', error);
        }
      }
    },

    shouldProactivelyRefresh(auth) {
      if (!auth?.token) {
        debugToken('No token available for proactive refresh check');
        return false;
      }
      if (!api.hasUsableRefreshToken(auth)) {
        debugToken('Skipping proactive refresh because no usable refresh token is available');
        return false;
      }
      const now = Date.now();
      const shouldRefresh = auth.exp <= now + state.skewMs && api.canRefresh(auth, now);
      
      if (shouldRefresh) {
        const timeToExp = Math.round((auth.exp - now) / 1000);
        debugToken('Token should be proactively refreshed (expires in %ds)', timeToExp);
      }
      
      return shouldRefresh;
    },

    isTerminalRefreshError(err) {
      // ✅ STEP 10: Circuit breaker errors are NOT terminal (user can retry after timeout)
      if (err?.code === 'CIRCUIT_OPEN') {
        return false;
      }

      // Missing refresh token in storage — session is unrecoverable, logout immediately
      if (err?.message && /refresh token not found/i.test(err.message)) {
        debugError('Terminal refresh error: refresh token absent from storage: %s', err.message);
        return true;
      }

      // Token validation errors are terminal - malformed tokens from backend require logout
      if (err?.message && /invalid authentication token/i.test(err.message)) {
        debugError('Terminal validation error detected: %s', err.message);
        return true;
      }

      const s = err?.response?.status;
      const body = err?.response?.data;
      const msg = (body?.error || body?.error_description || '').toString();
      const isTerminal = s === 401 || (s === 400 && /invalid_grant|invalid_request/i.test(msg));

      if (isTerminal) {
        debugError('Terminal refresh error detected: status=%d, message=%s', s, msg);
      }

      return isTerminal;
    },

    async refreshSingleFlight() {
      // If another tab is refreshing, wait for it
      if (state.externalRefreshInFlight) {
        debugStep4('step 4: external refresh in progress, waiting (with timeout protection)');
        debugRefresh('External refresh in progress, waiting for result');
        return state.externalRefreshInFlight;
      }

      // In-tab single-flight guard
      if (state._inFlightRefresh) {
        debugStep5('step 5: in-tab refresh already in progress (single-flight coalescing)');
        debugRefresh('In-tab refresh already in progress, awaiting existing promise');
        return state._inFlightRefresh;
      }

      // Check offline with error code
      if (navigator.onLine === false) {
        debugStep8('step 8: device offline - throwing OFFLINE error code');
        debugOffline('Offline, cannot refresh token');
        const error = new Error('Device is offline');
        error.code = 'OFFLINE';
        throw error;
      }

      debugStep5('step 5: starting new token refresh (single-flight leader)');
      debugRefresh('Starting token refresh');
      api.broadcastStart();

      // Create and store the in-flight promise
      state._inFlightRefresh = (async () => {
        try {
          // ✅ STEP 10: Wrap refresh with circuit breaker
          debugStep10('step 10: executing refresh through circuit breaker (5xx filtering)');
          const tokens = await circuitBreaker.execute(async () => {
            return await state.refreshFn();
          });

          debugStep10('step 10: circuit breaker execution successful');
          debugRefresh('Token refresh successful');
          api.persist(tokens);
          api.broadcastSuccess(tokens);
          return tokens;
        } catch (error) {
          debugError('Token refresh failed: %O', error);
          api.broadcastFail();
          throw error;
        } finally {
          // Clear the in-flight promise regardless of outcome
          debugStep5('step 5: clearing in-flight promise');
          state._inFlightRefresh = null;
        }
      })();

      return state._inFlightRefresh;
    },
  };

  // Init BroadcastChannel or storage fallback
  // IMPORTANT: Must be called AFTER api object is fully defined
  // The message handler needs access to api.persist(), api.broadcastSuccess(), etc.
  (function initChannel() {
    debugCrossTabs('Initializing cross-tab communication channel');
    
    // Create the message handler (now api and resetExternal are available)
    const messageHandler = createMessageHandler(state, resetExternal, api, circuitBreaker, EXTERNAL_REFRESH_TIMEOUT);

    // Try to initialize BroadcastChannel
    const bc = initBroadcastChannel(state.channelName);

    // Setup either BroadcastChannel or localStorage fallback
    if (bc) {
      setupBroadcastChannel(bc, state, messageHandler);
    } else {
      setupStorageFallback(state, messageHandler);
    }
    
    debugCrossTabs('Cross-tab communication channel initialized successfully');
  })();

  debugToken('TokenManager created successfully');

  // Auto-initialize offline detection if in browser environment
  if (typeof window !== 'undefined') {
    api.initialize();
  }

  return api;
}
